import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2,
  Save, Eye, EyeOff, Loader2, Mail,
} from "lucide-react";

/**
 * EmailTemplatesSection (v4.4.4) — visual "mini Word" editor for the
 * transactional email templates. The body is edited through a
 * contentEditable surface (same toolbar family as the rental-terms editor)
 * and stored as HTML. Variables are inserted at the cursor from a dropdown;
 * both `{tag}` and `{{tag}}` are expanded server-side.
 */
const TEMPLATE_META = {
  customer_booking_confirmation: {
    label: "Customer · Booking confirmation",
    hint:  "Sent to the customer after a booking is created or confirmed. The agreement PDF is attached.",
  },
  admin_new_booking_notification: {
    label: "Admin · New booking notification",
    hint:  "Sent to the ops mailbox whenever a new booking is created.",
  },
  customer_booking_cancellation: {
    label: "Customer · Booking cancellation",
    hint:  "Sent when an operator cancels the reservation. Auto-expired unpaid holds are excluded.",
  },
  customer_modification_approved: {
    label: "Customer · Modification approved",
    hint:  "Sent when an admin approves a change request. Includes the updated summary and the new agreement PDF.",
  },
  customer_modification_rejected: {
    label: "Customer · Modification rejected",
    hint:  "Sent when a change request is declined — the existing reservation stands unchanged.",
  },
  customer_abandoned_checkout: {
    label: "Customer · Abandoned checkout",
    hint:  "Recovery email for a basket that never became a booking. Use {resume_url} for the one-click link back.",
  },
  customer_booking_request_received: {
    label: "Customer · Booking request received",
    hint:  "Sent to the customer when they submit a 0%-deposit booking request, before staff approve or decline it.",
  },
};

const VARIABLES = [
  ["customer_name",        "Customer name"],
  ["booking_reference",    "Booking reference"],
  ["vehicle_title",        "Vehicle"],
  ["pickup_location",      "Pickup location"],
  ["pickup_date",          "Pickup date & time"],
  ["return_location",      "Return location"],
  ["return_date",          "Return date & time"],
  ["total_amount",         "Total amount"],
  ["deposit_amount",       "Advance deposit"],
  ["balance_due",          "Balance at pickup"],
  ["payment_method",       "Payment method"],
  ["bank_details",         "Bank transfer block"],
  ["brand_name",           "Brand name"],
  ["support_email",        "Support email"],
  ["support_phone",        "Support phone"],
  ["modification_summary", "Modification summary"],
  ["previous_total",       "Previous total"],
  ["new_total",            "New total"],
  ["price_difference",     "Price difference"],
  ["resume_url",           "Resume basket link"],
];

const SAMPLE = {
  customer_name: "Alexandra Testcase",
  booking_reference: "B8A2F09C",
  vehicle_title: "Peugeot 208 or similar",
  pickup_location: "Airport",
  pickup_date: "15/09/2027 10:00",
  return_location: "Airport",
  return_date: "18/09/2027 10:00",
  total_amount: "400.00",
  deposit_amount: "80.00",
  balance_due: "320.00",
  payment_method: "Bank transfer",
  bank_details: "<p><strong>Bank:</strong> Eurobank<br/><strong>IBAN:</strong> GR16 0260 0130 0000 1010 0000 001</p>",
  brand_name: "Brainart Rent",
  support_email: "support@brainart.gr",
  support_phone: "+30 210 0000000",
  modification_summary: "<ul><li>Return date: 18/09/2027 → <strong>20/09/2027</strong></li></ul>",
  previous_total: "400.00",
  new_total: "520.00",
  price_difference: "+120.00",
  resume_url: "https://carrental.brainart.app/book?resume=abc123",
};

const TOOLS = [
  { id: "bold",   label: "Bold",          Icon: Bold,        cmd: ["bold"] },
  { id: "italic", label: "Italic",        Icon: Italic,      cmd: ["italic"] },
  { id: "h2",     label: "Heading",       Icon: Heading2,    cmd: ["formatBlock", "<h2>"] },
  { id: "h3",     label: "Subheading",    Icon: Heading3,    cmd: ["formatBlock", "<h3>"] },
  { id: "ul",     label: "Bullet list",   Icon: List,        cmd: ["insertUnorderedList"] },
  { id: "ol",     label: "Numbered list", Icon: ListOrdered, cmd: ["insertOrderedList"] },
];

const render = (text) => {
  let out = text || "";
  for (const [k, v] of Object.entries(SAMPLE)) {
    out = out.split(`{{${k}}}`).join(v).split(`{${k}}`).join(v);
  }
  return out;
};

export default function EmailTemplatesSection() {
  const [tpls, setTpls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/email-templates")
      .then((r) => setTpls(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTpls([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-neutral-500">Loading templates…</p>;
  if (tpls.length === 0) return <p className="text-sm text-neutral-500">No templates.</p>;

  return (
    <div className="space-y-6" data-testid="email-templates-section">
      {tpls.map((t) => (
        <TemplateEditor
          key={t.key}
          template={t}
          onSaved={(next) => setTpls((prev) => prev.map((p) => (p.key === next.key ? next : p)))}
        />
      ))}
    </div>
  );
}

function TemplateEditor({ template, onSaved }) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [isActive, setIsActive] = useState(template.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const editorRef = useRef(null);

  const meta = TEMPLATE_META[template.key] || { label: template.key, hint: "" };

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = template.body || "<p><br/></p>";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.key]);

  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    setBody(editorRef.current?.innerHTML || "");
  };

  const insertVariable = (tag) => {
    if (!tag) return;
    editorRef.current?.focus();
    document.execCommand("insertText", false, `{${tag}}`);
    setBody(editorRef.current?.innerHTML || "");
  };

  const save = async () => {
    const draft = (editorRef.current?.innerHTML || body || "").trim();
    if (!draft) {
      toast.error("Template body cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${template.key}`, {
        subject, body: draft, is_active: isActive,
      });
      setBody(data.body);
      toast.success("Template saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-black/10 dark:border-white/10 rounded-lg p-4 space-y-3"
      data-testid={`tpl-${template.key}`}
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-1.5">
            <Mail size={12} /> Template
          </p>
          <h4 className="text-base font-bold uppercase tracking-wider text-slate-900 dark:text-white mt-1">{meta.label}</h4>
          <p className="text-[11px] text-neutral-500 mt-0.5">{meta.hint}</p>
        </div>
        <label className="text-xs inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid={`tpl-${template.key}-active`}
          />
          Active
        </label>
      </header>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">Subject</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-black/15 dark:border-white/15 rounded text-sm bg-white dark:bg-[#111827]"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid={`tpl-${template.key}-subject`}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Message</label>
          <button
            type="button"
            onClick={() => setPreviewOn((v) => !v)}
            className="text-xs px-2.5 py-1 rounded border border-black/10 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-1"
            data-testid={`tpl-${template.key}-preview-toggle`}
          >
            {previewOn ? <EyeOff size={11} /> : <Eye size={11} />}
            {previewOn ? "Hide preview" : "Preview email"}
          </button>
        </div>

        <div
          className="flex items-center flex-wrap gap-1 border border-black/15 dark:border-white/20 rounded-t px-2 py-1.5 bg-[#F9FAFB] dark:bg-[#111827]"
          data-testid={`tpl-${template.key}-toolbar`}
        >
          {TOOLS.map(({ id, label, Icon, cmd }) => (
            <button
              key={id}
              type="button"
              title={label}
              aria-label={label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec(...cmd)}
              className="p-2 rounded hover:bg-black/10 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-200"
              data-testid={`tpl-${template.key}-tool-${id}`}
            >
              <Icon size={15} />
            </button>
          ))}
          <button
            type="button"
            title="Insert link"
            aria-label="Insert link"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              // eslint-disable-next-line no-alert
              const url = window.prompt("Link URL", "https://");
              if (url) exec("createLink", url);
            }}
            className="p-2 rounded hover:bg-black/10 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-200"
            data-testid={`tpl-${template.key}-tool-link`}
          >
            <Link2 size={15} />
          </button>
          <span className="w-px h-5 bg-black/10 dark:bg-white/15 mx-1" />
          <select
            value=""
            onChange={(e) => { insertVariable(e.target.value); e.target.value = ""; }}
            className="text-xs px-2 py-1.5 rounded border border-black/15 dark:border-white/20 bg-white dark:bg-[#1F2937]"
            data-testid={`tpl-${template.key}-variables`}
          >
            <option value="">Insert variable…</option>
            {VARIABLES.map(([tag, label]) => (
              <option key={tag} value={tag}>{label} — {`{${tag}}`}</option>
            ))}
          </select>
        </div>

        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => setBody(e.currentTarget.innerHTML)}
          className="terms-rich w-full min-h-[220px] max-h-[55vh] overflow-y-auto bg-white dark:bg-[#111827] border border-t-0 border-black/15 dark:border-white/20 rounded-b p-4 text-sm leading-relaxed outline-none focus:border-black dark:focus:border-white"
          data-testid={`tpl-${template.key}-editor`}
        />
        <p className="text-[11px] text-neutral-500 mt-1.5">
          Variables render with real booking values when the email is sent. Use the
          dropdown to drop one in at the cursor.
        </p>
      </div>

      {previewOn && (
        <div
          className="border border-emerald-300/60 dark:border-emerald-700/50 bg-emerald-50/50 dark:bg-emerald-950/20 rounded p-3"
          data-testid={`tpl-${template.key}-preview`}
        >
          <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 dark:text-emerald-300 mb-2">
            Live preview · sample data
          </p>
          <p className="text-xs font-semibold">Subject: {render(subject)}</p>
          <div
            className="terms-rich mt-2 border-t border-black/10 pt-2 text-xs bg-white dark:bg-[#111827] p-3 rounded"
            dangerouslySetInnerHTML={{ __html: render(body) }}
          />
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-black text-white dark:bg-white dark:text-black text-sm px-4 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
          data-testid={`tpl-${template.key}-save`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save template
        </button>
      </div>
    </div>
  );
}
